import { observer } from 'mobx-react';
import * as React from 'react';
import { Redirect, Route, RouteComponentProps, HashRouter as Router, Switch } from 'react-router-dom';
import { AppState } from '../../../models/AppState';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { ErrorMessagesProvider } from '../../../web/components/errorMessages/ErrorMessagesProvider';
import { RouteWithLoggedPageView } from '../../../web/components/PrivateRoute';
import { ScrollRestoration } from '../../../web/components/ScrollRestoration';
import { Login } from '../../../web/containers/Login';
import { Logout } from '../../../web/containers/Logout';
import { AdminPrivateRoute } from '../../components/AdminPrivateRoute';
import { AdminFullscreenModal } from '../../components/fullscreen/AdminFullscreenModal';
import { AdminAccountBlogEditorPage } from '../../pages/AdminAccountBlogEditorPage';
import { AdminAccountBlogImportHistoryPage } from '../../pages/AdminAccountBlogImportHistoryPage';
import { AdminAccountBlogSettingsPage } from '../../pages/AdminAccountBlogSettingsPage';
import { AdminAccountBlogTemplateDesignerPage } from '../../pages/AdminAccountBlogTemplateDesignerPage';
import { AdminAccountConfigureIntegrationPage } from '../../pages/AdminAccountConfigureIntegrationPage';
import { AdminAccountConfirmationPage } from '../../pages/AdminAccountConfirmationPage';
import { AdminAccountContentCalendarPage } from '../../pages/AdminAccountContentCalendarPage';
import { AdminAccountCreateChargifyPage } from '../../pages/AdminAccountCreateChargifyPage';
import { AdminAccountDataRefreshPage } from '../../pages/AdminAccountDataRefreshPage';
import { AdminAccountDetailsPage } from '../../pages/AdminAccountDetailsPage';
import { AdminAccountDetailsV2Page } from '../../pages/AdminAccountDetailsV2Page';
import { AdminAccountEditAutomationPage } from '../../pages/AdminAccountEditAutomationPage';
import { AdminAccountIntegrationsPage } from '../../pages/AdminAccountIntegrationsPage';
import { AdminAccountNewAutomationPage } from '../../pages/AdminAccountNewAutomationPage';
import { AdminAccountsPage } from '../../pages/AdminAccountsPage';
import { AdminAgentTemplatesPage } from '../../pages/AdminAgentTemplatesPage';
import { AdminAutomationsPage } from '../../pages/AdminAutomationsPage';
import { AdminAutomationTestingPage } from '../../pages/AdminAutomationTestingPage';
import { AdminBillingOpenInvoicesPage } from '../../pages/AdminBillingOpenInvoicesPage';
import { AdminCampaignDebugPage } from '../../pages/AdminCampaignDebugPage';
import { AdminCampaignsGhostCalendarPage } from '../../pages/AdminCampaignsGhostCalendarPage';
import { AdminCampaignsPage } from '../../pages/AdminCampaignsPage';
import { AdminCreateAccountPage } from '../../pages/AdminCreateAccountPage';
import { AdminDevToolsPage } from '../../pages/AdminDevToolsPage';
import { AdminNotFoundPage } from '../../pages/AdminNotFoundPage';
import { AdminReportsDailyPage } from '../../pages/AdminReportsDailyPage';
import { AdminReportsMonthlyPage } from '../../pages/AdminReportsMonthlyPage';
import { AdminReportsPage } from '../../pages/AdminReportsPage';
import { AdminToolsPage } from '../../pages/AdminToolsPage';
import { AdminPricingWizardPage } from '../../pages/AdminPricingWizardPage';
import { AdminLogMeetingNotesPage } from '../../pages/AdminLogMeetingNotesPage';
import { AdminAPIKeysPage } from '../account/apiKeys/AdminAPIKeysPage';

function _AdminRouter() {
	const userSession = useAdminUserSession();
	React.useEffect(() => {
		if (window.location.href.includes('secure.levitate.ai/admin.html')) {
			window.location.replace('https://admin.levitate.ai/' + window.location.hash);
		}
	}, []);
	const onRenderLogin = (props: RouteComponentProps<any>) => {
		return <Login {...props} loginMode='admin' userSession={userSession} />;
	};
	return (
		<Router>
			<>
				{!!AppState.errorMessages && <ErrorMessagesProvider errorMessageViewModel={AppState.errorMessages} />}
				<Switch>
					<Route exact={true} path='/'>
						<Redirect to='/login' />
					</Route>
					<RouteWithLoggedPageView path='/login' pageViewLogName='login' render={onRenderLogin} />
					<RouteWithLoggedPageView path='/logout' pageViewLogName='logout'>
						<Logout userSession={userSession} />
					</RouteWithLoggedPageView>
					<AdminPrivateRoute>
						<Switch>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:id/integration/:integrationType'
								userSession={userSession}
							>
								<AdminAccountConfigureIntegrationPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute exact={true} path='/accounts/:id/automations/new' userSession={userSession}>
								<AdminAccountNewAutomationPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute exact={true} path='/accounts/:accountId/automations/:id' userSession={userSession}>
								<AdminAccountEditAutomationPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute exact path='/accounts/:accountId/import/data-refresh' userSession={userSession}>
								<AdminAccountDataRefreshPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute exact={true} path='/accounts' pageViewLogName='admin/dashboard'>
								<AdminAccountsPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:id/create-account-chargify'
								pageViewLogName='admin/create-account-chargify'
							>
								<AdminAccountCreateChargifyPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute exact={true} path='/accounts/create-account' pageViewLogName='admin/create-account'>
								<AdminCreateAccountPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/create-account-v2'
								pageViewLogName='admin/create-account-v2'
							>
								<AdminCreateAccountPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:id/integrations/:integrationProvider/:tab?'
								pageViewLogName='admin/account-integrations'
							>
								<AdminAccountIntegrationsPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:id/content-calendar'
								pageViewLogName='admin/account-content-calendar'
							>
								<AdminAccountContentCalendarPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute exact={true} path='/accounts/:id/blog-settings' pageViewLogName='admin/blog-settings'>
								<AdminAccountBlogSettingsPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:id/blog-import-history'
								pageViewLogName='admin/blog-import-history'
							>
								<AdminAccountBlogImportHistoryPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute exact={true} path='/accounts/:id/blog-editor' pageViewLogName='admin/blog-editor'>
								<AdminAccountBlogEditorPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:id/template-designer'
								pageViewLogName='admin/template-designer'
							>
								<AdminAccountBlogTemplateDesignerPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:id/confirmation'
								pageViewLogName='admin/account-confirmation'
							>
								<AdminAccountConfirmationPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:accountId/api-keys'
								pageViewLogName='admin/api-keys'
								userSession={userSession}
							>
								<AdminAPIKeysPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/accounts/:accountId/automations/:id/test'
								pageViewLogName='admin/automation-testing'
							>
								<AdminAutomationTestingPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute path='/accounts-v2/:accountId' pageViewLogName='admin/account-detail-v2'>
								<AdminAccountDetailsV2Page />
							</AdminPrivateRoute>
							<AdminPrivateRoute path='/accounts/:accountId' pageViewLogName='admin/account-detail'>
								<AdminAccountDetailsPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute path='/devtools' pageViewLogName='admin/devtools'>
								<AdminDevToolsPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute exact={true} path='/tools/pricing-wizard' pageViewLogName='admin/tools/pricing-wizard'>
								<AdminPricingWizardPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								exact={true}
								path='/tools/log-meeting-notes'
								pageViewLogName='admin/tools/log-meeting-notes'
							>
								<AdminLogMeetingNotesPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute path='/tools' pageViewLogName='admin/tools'>
								<AdminToolsPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute path='/billing/openInvoices' exact={true} pageViewLogName='admin/billing/openInvoices'>
								<AdminBillingOpenInvoicesPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute path='/reports/daily' pageViewLogName='admin/reports/daily'>
								<AdminReportsDailyPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute path='/reports/monthly' pageViewLogName='admin/reports/monthly'>
								<AdminReportsMonthlyPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute path='/reports' pageViewLogName='admin/reports'>
								<AdminReportsPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute path='/campaigns/ghost-calendar' exact={true} pageViewLogName='admin/ghost-calendar'>
								<AdminCampaignsGhostCalendarPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute
								path='/campaigns/:campaignId/debug'
								exact={true}
								pageViewLogName='admin/campaign-debug'
							>
								<AdminCampaignDebugPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute path='/campaigns' pageViewLogName='admin/campaigns'>
								<AdminCampaignsPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute path='/automations' pageViewLogName='admin/automations'>
								<AdminAutomationsPage />
							</AdminPrivateRoute>
							<AdminPrivateRoute path='/ai-agent-templates' pageViewLogName='admin/ai-agent-templates'>
								<AdminAgentTemplatesPage />
							</AdminPrivateRoute>
							{/* This must be last.*/}
							<AdminPrivateRoute pageViewLogName='admin/not-found'>
								<AdminNotFoundPage />
							</AdminPrivateRoute>
						</Switch>
					</AdminPrivateRoute>
				</Switch>
				<AdminFullscreenModal />
			</>
			<ScrollRestoration />
		</Router>
	);
}

export const AdminRouter = observer(_AdminRouter);
